import { Link, useNavigate, useLocation } from 'react-router-dom'
import Footer from './Footer'
import { ChevronLeftIcon, PrimaryButton } from './ui'

export interface CheckYourDetailsState {
  firstName?: string
  lastName?: string
  email?: string
  /** @deprecated Prefer policyNumbers; kept for older navigations */
  policyNumber?: string
  policyNumbers?: string[]
  linkAdditionalPolicy?: boolean
}

function formatDisplayName(firstName?: string, lastName?: string): string {
  const first = (firstName ?? '').trim()
  const last = (lastName ?? '').trim()
  if (!first && !last) return '—'
  return [first, last].filter(Boolean).join(' ')
}

export default function CheckYourDetails() {
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state ?? {}) as CheckYourDetailsState

  const displayName = formatDisplayName(state.firstName, state.lastName)
  const email = (state.email ?? '').trim() || '—'
  const policyNums =
    state.policyNumbers && state.policyNumbers.length > 0
      ? state.policyNumbers.map((p) => String(p).trim()).filter(Boolean)
      : (state.policyNumber ?? '').trim()
        ? [(state.policyNumber ?? '').trim()]
        : []
  const policyNumberLabel =
    policyNums.length === 0 ? '—' : policyNums.length === 1 ? policyNums[0] : policyNums.join(', ')

  return (
    <div className="bg-background-tertiary flex flex-col items-center justify-center min-h-screen w-full">
      <main id="main" className="flex w-full flex-col items-center justify-center">
      <div className="flex flex-col gap-[32px] items-center justify-center px-[16px] py-[80px] relative w-full max-w-lg">
        {/* Back link */}
        <Link
          to="/confirm-details"
          className="cursor-pointer flex gap-[4px] items-end p-0 relative w-full"
        >
          <div className="overflow-clip relative shrink-0 size-5 text-text-primary-900">
            <ChevronLeftIcon />
          </div>
          <p className="font-body font-semibold text-sm leading-sm text-text-primary-900">
            Back
          </p>
        </Link>

        {/* Teal header with logo */}
        <div className="bg-background-brand-solid flex flex-col items-center justify-center px-[24px] py-[20px] relative rounded-t-xl w-full">
          <div className="flex items-start relative w-[200px]">
            <img
              src="/Clip path group.svg"
              alt="Club 4X4 Insurance"
              className="h-8 w-auto object-contain brightness-0 invert"
            />
          </div>
        </div>

        {/* White card */}
        <div className="bg-background-primary flex flex-col gap-[24px] items-start px-[24px] py-[32px] relative rounded-b-xl shadow-lg w-full">
          <h1 className="font-display font-semibold text-display-xs leading-tight text-text-primary-900 w-full">
            Check your details
          </h1>

          <p className="font-body font-regular text-md leading-md text-text-tertiary-600 w-full">
            We couldn&apos;t find a policy that matches the information you provided:
          </p>

          <ul className="font-body font-regular text-md leading-md text-text-primary-900 list-disc list-inside space-y-2 w-full">
            <li>Name: {displayName}</li>
            <li>Email address: {email}</li>
            <li>
              Policy number{policyNums.length > 1 ? 's' : ''}: {policyNumberLabel}
            </li>
          </ul>

          <p className="font-body font-regular text-md leading-md text-text-tertiary-600 w-full">
            Please double-check that:
          </p>
          <ul className="font-body font-regular text-md leading-md text-text-primary-900 list-disc list-inside space-y-2 w-full">
            <li>You have an active Club 4X4 policy</li>
            <li>Your details match your policy documents</li>
          </ul>

          <p className="font-body font-regular text-md leading-md text-text-tertiary-600 w-full">
            You may have entered the wrong details by mistake. Check your answers and try again.
          </p>

          <PrimaryButton
            type="button"
            onClick={() =>
              navigate('/confirm-details', {
                replace: true,
                state: {
                  firstName: state.firstName,
                  lastName: state.lastName,
                  email: state.email,
                  policyNumbers: policyNums.length > 0 ? policyNums : undefined,
                  policyNumber: policyNums[0],
                  linkAdditionalPolicy: state.linkAdditionalPolicy,
                },
              })
            }
            fullWidth
          >
            Try again
          </PrimaryButton>

          <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600 w-full">
            Need help? Call us on{' '}
            <a href="tel:1300296296" className="font-semibold text-foreground-brand-primary-600 hover:underline">
              1300 296 296
            </a>
          </p>
        </div>
      </div>
      </main>

      <Footer />
    </div>
  )
}
